import { Alert, Button, Checkbox, Input, InputNumber, Progress, Radio, Result, Spin } from "antd";
import dayjs from "dayjs";
import { type ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  type IntakeAnswers,
  type IntakeField,
  type IntakePublicForm,
  getIntakePublicForm,
  submitIntakePublicForm,
} from "../../service/intake.service";
import styles from "./intake.module.scss";

type FieldErrors = Record<string, string>;

const isBlank = (v: unknown) =>
  v === undefined ||
  v === null ||
  v === "" ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && !Array.isArray(v) && Object.keys(v as object).length === 0);

/** Mirrors the server rules closely enough to catch the common mistakes before a round trip. */
const validatePage = (fields: IntakeField[], answers: IntakeAnswers): FieldErrors => {
  const errors: FieldErrors = {};
  for (const f of fields) {
    const v = answers[f.key];
    if (isBlank(v)) {
      if (f.required) errors[f.key] = "This answer is required.";
      continue;
    }
    if (f.type === "number" && typeof v === "number" && f.min !== undefined && f.max !== undefined) {
      if (!Number.isInteger(v) || v < f.min || v > f.max) errors[f.key] = `Must be between ${f.min} and ${f.max}.`;
    }
    if (f.type === "checkbox" && Array.isArray(v) && f.options) {
      const exclusive = f.options.filter((o) => o.exclusive && v.includes(o.key));
      if (exclusive.length && v.length > 1)
        errors[f.key] = `“${exclusive[0].label}” cannot be combined with other choices.`;
    }
    if (f.type === "family_history" && v && typeof v === "object" && !Array.isArray(v) && f.options) {
      const keys = Object.keys(v);
      const exclusive = f.options.filter((o) => o.exclusive && keys.includes(o.key));
      if (exclusive.length && keys.length > 1)
        errors[f.key] = `“${exclusive[0].label}” cannot be combined with other conditions.`;
    }
  }
  return errors;
};

function FamilyHistoryField({
  field,
  value,
  onChange,
}: {
  field: IntakeField;
  value: Record<string, string[]>;
  onChange: (v: Record<string, string[]>) => void;
}) {
  const options = field.options ?? [];
  const relatives = field.relatives ?? [];
  const checked = Object.keys(value);
  const toggleCondition = (key: string, on: boolean) => {
    const next = { ...value };
    if (on) next[key] = next[key] ?? [];
    else delete next[key];
    onChange(next);
  };
  const toggleRelative = (cond: string, rel: string, on: boolean) => {
    const rels = new Set(value[cond] ?? []);
    if (on) rels.add(rel);
    else rels.delete(rel);
    onChange({ ...value, [cond]: Array.from(rels) });
  };
  return (
    <>
      <div className={styles.checkGrid}>
        {options.map((o) => (
          <Checkbox
            key={o.key}
            checked={checked.includes(o.key)}
            onChange={(e) => toggleCondition(o.key, e.target.checked)}
          >
            {o.label}
          </Checkbox>
        ))}
      </div>
      {checked.filter((c) => !options.find((o) => o.key === c)?.exclusive).length > 0 && (
        <>
          <div className={styles.hint} style={{ marginTop: 12 }}>
            For each condition checked above, indicate affected family member(s):
          </div>
          <table className={styles.famTable}>
            <thead>
              <tr>
                <th>Condition</th>
                {relatives.map((r) => (
                  <th key={r.key}>{r.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {checked
                .filter((c) => !options.find((o) => o.key === c)?.exclusive)
                .map((c) => (
                  <tr key={c}>
                    <td>{options.find((o) => o.key === c)?.label ?? c}</td>
                    {relatives.map((r) => (
                      <td key={r.key}>
                        <Checkbox
                          checked={(value[c] ?? []).includes(r.key)}
                          onChange={(e) => toggleRelative(c, r.key, e.target.checked)}
                          aria-label={`${options.find((o) => o.key === c)?.label ?? c}: ${r.label}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function Field({
  field,
  value,
  error,
  onChange,
}: {
  field: IntakeField;
  value: IntakeAnswers[string] | undefined;
  error?: string;
  onChange: (v: IntakeAnswers[string] | undefined) => void;
}) {
  const id = `intake-${field.key}`;
  let control: ReactElement;
  switch (field.type) {
    case "text":
      control = (
        <Input id={id} value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} maxLength={2000} />
      );
      break;
    case "textarea":
      control = (
        <Input.TextArea
          id={id}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 8 }}
          maxLength={2000}
        />
      );
      break;
    case "number":
      control = (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <InputNumber
            id={id}
            min={field.min}
            max={field.max}
            precision={0}
            value={(value as number) ?? null}
            onChange={(v) => onChange(v === null ? undefined : Number(v))}
            style={{ width: 120 }}
          />
          {field.max === 10 ? <span>/ 10</span> : null}
        </span>
      );
      break;
    case "yes_no":
      control = (
        <Radio.Group id={id} value={value ?? null} onChange={(e) => onChange(e.target.value)}>
          <Radio value="yes">Yes</Radio>
          <Radio value="no">No</Radio>
        </Radio.Group>
      );
      break;
    case "radio":
      control = (
        <Radio.Group id={id} value={value ?? null} onChange={(e) => onChange(e.target.value)}>
          <div className={styles.checkGrid}>
            {(field.options ?? []).map((o) => (
              <Radio key={o.key} value={o.key}>
                {o.label}
              </Radio>
            ))}
          </div>
        </Radio.Group>
      );
      break;
    case "checkbox": {
      const selected = (value as string[]) ?? [];
      const exclusiveOn = (field.options ?? []).some((o) => o.exclusive && selected.includes(o.key));
      control = (
        <div className={styles.checkGrid}>
          {(field.options ?? []).map((o) => (
            <Checkbox
              key={o.key}
              checked={selected.includes(o.key)}
              disabled={exclusiveOn && !selected.includes(o.key)}
              onChange={(e) => {
                if (e.target.checked) onChange(o.exclusive ? [o.key] : [...selected, o.key]);
                else onChange(selected.filter((k) => k !== o.key));
              }}
            >
              {o.label}
            </Checkbox>
          ))}
        </div>
      );
      break;
    }
    case "family_history":
      control = (
        <FamilyHistoryField
          field={field}
          value={(value as Record<string, string[]>) ?? {}}
          onChange={(v) => onChange(v)}
        />
      );
      break;
    default:
      control = <Input id={id} />;
  }
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {field.label}
        {field.required && <span className={styles.required}>*</span>}
      </label>
      {field.hint && <div className={styles.hint}>{field.hint}</div>}
      {control}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

/** The patient-facing intake form behind a bearer link. */
export default function IntakeFormPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [data, setData] = useState<IntakePublicForm | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [answers, setAnswers] = useState<IntakeAnswers>({});
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    getIntakePublicForm(token)
      .then((r) => setData(r.data))
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        setLoadError(typeof detail === "string" ? detail : "This link is not valid.");
      });
  }, [token]);

  const pages = data?.form.pages ?? [];
  const current = pages[page];
  const pageFields = useMemo(() => current?.sections.flatMap((s) => s.fields) ?? [], [current]);

  const setAnswer = useCallback((key: string, v: IntakeAnswers[string] | undefined) => {
    setAnswers((prev) => {
      const next = { ...prev };
      if (isBlank(v)) delete next[key];
      else next[key] = v as IntakeAnswers[string];
      return next;
    });
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const goNext = () => {
    const errs = validatePage(pageFields, answers);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setPage((p) => Math.min(p + 1, pages.length - 1));
    window.scrollTo({ top: 0 });
  };

  const submit = async () => {
    const errs = validatePage(pageFields, answers);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitIntakePublicForm(token, answers);
      setDone(true);
    } catch (e) {
      const body = (e as { response?: { data?: { detail?: string; errors?: FieldErrors } } })?.response?.data;
      if (body?.errors) {
        setErrors(body.errors);
        // Jump to the first page that has a problem so the patient can see it.
        const firstKey = Object.keys(body.errors)[0];
        const idx = pages.findIndex((p) => p.sections.some((s) => s.fields.some((f) => f.key === firstKey)));
        if (idx >= 0) setPage(idx);
      }
      setSubmitError(body?.detail ?? "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className={styles.body}>
        <div className={styles.shell}>
          <Result status="warning" title="This link can't be opened" subTitle={loadError} />
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className={styles.body}>
        <div className={styles.shell} style={{ textAlign: "center", paddingTop: 80 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }
  if (done || data.status === "submitted") {
    return (
      <div className={styles.body}>
        <div className={styles.shell}>
          <div className={styles.done}>
            <Result
              status="success"
              title="Thank you"
              subTitle="Your intake form has been received. Your care team will review it before your visit."
            />
          </div>
        </div>
      </div>
    );
  }

  const { patient } = data;
  const isLast = page === pages.length - 1;

  return (
    <div className={styles.body}>
      <div className={styles.shell}>
        <div className={styles.header}>
          <div>
            <div className={styles.pageTitle}>{patient.name}</div>
            <div className={styles.headerMeta}>
              {patient.date_of_birth ? `DOB: ${dayjs(patient.date_of_birth).format("MM/DD/YYYY")}` : null}
              {patient.age !== null ? `   Age: ${patient.age}` : null}
              {patient.gender ? `   Gender: ${patient.gender}` : null}
            </div>
            {data.practice_name && <div className={styles.headerMeta}>{data.practice_name}</div>}
          </div>
          <div style={{ textAlign: "right" }}>
            <div className={styles.pageTitle}>{data.form.title}</div>
            <div className={styles.pageSubtitle}>
              Page {page + 1} of {pages.length} — {current.title}
            </div>
          </div>
        </div>

        <Progress
          percent={Math.round(((page + 1) / pages.length) * 100)}
          showInfo={false}
          size="small"
          style={{ marginBottom: 16 }}
          aria-label={`Page ${page + 1} of ${pages.length}`}
        />

        {submitError && <Alert type="error" showIcon message={submitError} style={{ marginBottom: 16 }} />}

        {current.sections.map((section) => (
          <div className={styles.section} key={section.key}>
            <h3 className={styles.sectionTitle}>{section.title}</h3>
            {section.fields.map((f) => (
              <Field
                key={f.key}
                field={f}
                value={answers[f.key]}
                error={errors[f.key]}
                onChange={(v) => setAnswer(f.key, v)}
              />
            ))}
          </div>
        ))}

        <div className={styles.footer}>
          <Button disabled={page === 0 || submitting} onClick={() => setPage((p) => p - 1)}>
            Back
          </Button>
          {isLast ? (
            <Button type="primary" loading={submitting} onClick={submit}>
              Submit
            </Button>
          ) : (
            <Button type="primary" onClick={goNext}>
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
